import React from 'react';

import {
  Box,
  Button,
  Card as MuiCard,
  CardHeader,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
  type CardProps as MuiCardProps,
  type TypographyProps,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

export type CardColor = 'cherry' | 'lime' | 'warmOrange';

export interface CardAction {
  label: string;
  onClick: () => void;
  variant?: 'contained' | 'outlined';
}

export interface CardProps extends Omit<MuiCardProps, 'title' | 'variant'> {
  /** Card fill when `dark` is set. Without it, dark is charcoal. */
  color?: CardColor;
  /** Charcoal (or coloured) fill with light text. */
  dark?: boolean;
  /** SIZE only: 'compact' = h5 title, tighter padding. */
  variant?: 'default' | 'compact';
  title: string;
  /** One line, or several stacked secondary lines. */
  subtitle?: string | string[];
  /** Optional status chip shown under the subtitle (e.g. a count badge). */
  chip?: React.ReactNode;
  /** Prototype extension: override the title's Typography variant. */
  titleVariant?: TypographyProps['variant'];
  progress?: number;
  primaryAction?: CardAction;
  /**
   * Off for a tappable card that shouldn't show its chevron, like a webinar
   * in Help's swipe row.
   */
  chevron?: boolean;
}

/**
 * The consolidated card. One surface for plain, dark and navigational cards:
 * passing `onClick` makes a card navigational, which gives it the press state
 * and a chevron.
 *
 * The squiggle is gone. It was opt-in and, in the end, opted into by a single
 * Storybook control and nothing else — the real squiggles were llama
 * SquiggleCards on Home, and those were tinted from a three-colour palette that
 * never matched the Figma. `avatar`, `chevron`, `secondaryAction` and
 * `squiggleFlipped` went at the same time: twenty-six call sites, none of them
 * used any of the four.
 */
const Card = ({
  color,
  dark = false,
  variant = 'default',
  title,
  subtitle,
  chip,
  titleVariant,
  progress,
  primaryAction,
  chevron = true,
  sx,
  ...cardProps
}: CardProps) => {
  const theme = useTheme();
  const c = (theme.palette as any).custom;

  const darkFill: Record<CardColor, string> = {
    cherry: c.cherry[800],
    lime: c.limeGreen[800],
    warmOrange: c.warmOrange[800],
  };
  const surface = dark ? (color ? darkFill[color] : c.charcoal) : undefined;
  const onDark = c.neutral[50];

  const isCompact = variant === 'compact';
  const hasActions = Boolean(primaryAction);
  const hasProgress = typeof progress === 'number';
  // A tappable card is a navigational one, so it gets the chevron.
  const showChevron = chevron && Boolean(cardProps.onClick);

  const subtitleLines =
    subtitle === undefined ? [] : Array.isArray(subtitle) ? subtitle : null;

  return (
    // `listItem` is llama's press state — pointer, a 120ms transform and a
    // scale(0.98) on active, no shadow. This file used to write those three
    // values out in an sx; they were already in the theme.
    <MuiCard
      variant={cardProps.onClick ? 'listItem' : undefined}
      {...cardProps}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        ...(dark && {
          bgcolor: surface,
          borderColor: 'transparent',
          color: onDark,
          '& .MuiCardHeader-title': { color: onDark },
          '& .MuiCardHeader-subheader': { color: c.neutral[200] },
          '& .MuiTypography-caption': { color: onDark },
        }),
        ...sx,
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Title and subtitle in one Stack, passed as CardHeader's `title`.
            CardHeader's own two slots put them in a `display: block` content div,
            which means the space between them is whatever line-height leaves and
            there is no gap to change — not in the theme, not at a call site, and
            not in drill mode, which is where it was noticed. A flex parent gives
            them a real one.
            `spacing={0}` on purpose: that is what CardHeader was already giving
            them, so fifteen cards look exactly as they did and the lever now
            exists to be moved. Move it here, not per card. */}
        <CardHeader
          disableTypography
          title={
            <Stack sx={{ alignItems: 'flex-start' }} spacing={0}>
              <Typography
                variant={titleVariant ?? (isCompact ? 'h5' : 'h3')}
                color={dark ? onDark : undefined}
              >
                {title}
              </Typography>
              {subtitleLines ? (
                <Stack sx={{ alignItems: 'flex-start' }} spacing={0.5}>
                  {subtitleLines.map((line, i) => (
                    <Typography
                      key={i}
                      variant='body1'
                      color={dark ? c.neutral[200] : 'text.secondary'}
                    >
                      {line}
                    </Typography>
                  ))}
                  {chip}
                </Stack>
              ) : (
                <>
                  {subtitle && (
                    <Typography
                      variant={isCompact ? 'h6' : 'body1'}
                      color={dark ? c.neutral[200] : 'text.secondary'}
                    >
                      {subtitle}
                    </Typography>
                  )}
                  {chip && <Box sx={{ pt: 1 }}>{chip}</Box>}
                </>
              )}
            </Stack>
          }
          action={
            showChevron ? (
              <Icon icon='chevron-right' color={dark ? onDark : undefined} />
            ) : undefined
          }
          sx={{
            '& .MuiCardHeader-action': { ml: 2, mr: 0, alignSelf: 'center' },
            // A flex item will not shrink below its longest unbreakable word
            // unless it is told it can. Without this, a narrow card with a long
            // title — "Care assessment" in a half-width pair — pushes the
            // chevron past the card's own edge, where `overflow: hidden` eats
            // it: the card looks like it is not tappable while every other one
            // beside it does.
            '& .MuiCardHeader-content': { minWidth: 0 },
            ...(isCompact ? { py: 2.5, px: 2.5 } : {}),
          }}
        />
        {hasProgress && (
          <Box sx={{ px: isCompact ? 2.5 : 2, pb: isCompact ? 2.5 : 2 }}>
            <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
              <LinearProgress
                variant='determinate'
                value={progress}
                sx={{
                  flex: 1,
                  ...(dark && {
                    bgcolor: alpha(onDark, 0.24),
                    '& .MuiLinearProgress-bar': { bgcolor: onDark },
                  }),
                }}
              />
              {!isCompact && (
                <Typography variant='caption'>
                  {Math.round(progress!)}%
                </Typography>
              )}
            </Stack>
          </Box>
        )}
        {hasActions && (
          <Stack spacing={2} sx={{ p: 2, pt: 0 }}>
            {primaryAction && (
              <Button
                variant={primaryAction.variant ?? 'contained'}
                fullWidth
                onClick={primaryAction.onClick}
                sx={
                  dark
                    ? {
                        bgcolor: onDark,
                        color: surface,
                        '&:hover': { bgcolor: c.neutral[200] },
                      }
                    : undefined
                }
              >
                {primaryAction.label}
              </Button>
            )}
          </Stack>
        )}
      </Box>
    </MuiCard>
  );
};

Card.displayName = 'Card';
export default Card;
